import { Injectable } from '@angular/core';
import { of } from 'rxjs';
import { delay, takeUntil } from 'rxjs/operators';
import { BaseComponent } from '../../core';
import { __TYPE__, ResponseError, UuidAuthResponse } from '../../models';
import { ServerCallerService } from '../server-caller/server-caller.service';

@Injectable()
export abstract class AbstractAuthenticationService extends BaseComponent {
  constructor(protected serverCaller: ServerCallerService) {
    super();
  }

  public async logout(): Promise<boolean> {
    return true;
  }

  public async loginAndReturnToken(
    username: string,
    password: string
  ): Promise<string> {
    const url = '/authorization';

    const response = await this.serverCaller.post(url, {
      username,
      password,
      __type__: __TYPE__.BASIC_CREDENTIALS,
    });

    return response?.headers?.get('Authorization');
  }

  public async loginWithPinOrPasswordAndReturnToken(
    username: string,
    passwordOrPin: string
  ): Promise<string> {
    const url = '/authorization';

    let response;

    try {
      response = await this.serverCaller.post(url, {
        username,
        pin: passwordOrPin,
        __type__: __TYPE__.BASIC_CREDENTIALS,
      });
    } catch (e: any) {
      const error: ResponseError = e?.error;

      if (error.status === 401) {
        response = await this.serverCaller.post(url, {
          username,
          password: passwordOrPin,
          __type__: __TYPE__.BASIC_CREDENTIALS,
        });
      } else {
        throw e;
      }
    }

    return response?.headers?.get('Authorization');
  }

  public async createChangePasswordRequest(
    username: string | undefined | null,
    password: string | undefined | null
  ): Promise<string> {
    const url = '/v1/password-change-requests';
    const response = await this.serverCaller.post(url, {
      username,
      newPassword: password,
    });

    return response?.body?.id;
  }

  // return token
  public async resolveChangePasswordRequest(
    passwordRequestId: string | undefined,
    code: string | undefined | null
  ): Promise<string> {
    const url = `/v1/password-change-requests/${passwordRequestId}?code=${code}&alsoConfirmEmailOrPhoneNr=true`;
    const response = await this.serverCaller.delete(url);
    return response?.body?.token;
  }

  /**
   * Authenticate by using UUID in unsecured link with some retry due to Backend need time to activate unsecured link
   * the time of retry can be specified.
   * @param   {String}            uuid                      :  uuid parsed from unsecured link
   * @param   {Number}            numberOfRetry             :  max number of retry
   * @return  {UuidAuthResponse}                            :  it contains BoId, access token and maybe also appointment Id
   * */
  public async uuidAuthAutoRetry(
    uuid: string,
    numberOfRetry = 20
  ): Promise<UuidAuthResponse> {
    const timeOut = 100; // ms

    let retryCounter = 0;
    let uuidAuthResponse: UuidAuthResponse;
    let error;

    do {
      try {
        uuidAuthResponse = await this.uuidAuthTimeOutPromise(uuid, timeOut);
        if (!uuidAuthResponse) {
          retryCounter += 1;
        } else {
          break;
        }
      } catch (e) {
        error = e;
        retryCounter += 1;
      }
    } while (retryCounter < numberOfRetry);

    if (!uuidAuthResponse && !!error) {
      throw error;
    }

    return uuidAuthResponse || null;
  }

  /**
   * Authenticate by using UUID after some given seconds
   * @param   {String}            uuid                :  uuid parsed from unsecured link
   * @param   {Number}            waitingTimeInMs     :  waiting time in milli second
   * @return  {Promise}                               :  UuidAuthResponse in promise type
   * */
  private uuidAuthTimeOutPromise(
    uuid: string,
    waitingTimeInMs: number
  ): Promise<UuidAuthResponse> {
    return new Promise((resolve, reject) => {
      of('')
        .pipe(takeUntil(this.componentDestroyed), delay(waitingTimeInMs))
        .subscribe(() =>
          this.uuidAuth(uuid)
            .then((uuidAuthResponse) => {
              resolve(uuidAuthResponse);
            })
            .catch((e) => reject(e))
        );
    });
  }

  /**
   * Authenticate by using UUID in unsecured link
   * @param   {String}   uuid  : uuid parsed from unsecured link
   * @return  {Promise}        : UuidAuthResponse promise contains BoId, access token and maybe also appointment Id
   * */
  private async uuidAuth(uuid: string): Promise<UuidAuthResponse> {
    const url = `/uuid-authorization/${uuid}`;

    const response = await this.serverCaller.get(url);

    return response?.body;
  }
}
